import { Check, ChevronDown, Filter, Footprints, Info, MapPin, Search, SlidersHorizontal, Users, WalletCards, X } from 'lucide-react';
import { FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../components/EmptyState';
import { HostelCard } from '../components/HostelCard';
import { StatusNotice } from '../components/StatusNotice';
import { SelectMenu, type SelectMenuOption } from '../components/ui/SelectMenu';
import { api } from '../lib/api';
import { applyBudgetRange, budgetRangeFromParams, budgetRangeLabel, budgetRanges, occupancyOptions } from '../lib/searchOptions';
import type { HostelSummary } from '../lib/types';

const amenityOptions = [
  { key: 'water_storage', label: 'Reliable water' },
  { key: 'security', label: 'Security' },
  { key: 'kitchen', label: 'Kitchen access' },
  { key: 'study_area', label: 'Study area' },
  { key: 'internet', label: 'Wi-Fi / internet' },
  { key: 'parking', label: 'Parking' },
];

const campusOptions: SelectMenuOption[] = [
  { value: 'uds-tamale-dungu', label: 'UDS · Tamale', description: 'Tamale Campus (Dungu)' },
  { value: 'uds-nyankpala', label: 'UDS · Nyankpala', description: 'Nyankpala Campus' },
];

const sortOptions: SelectMenuOption[] = [
  { value: 'best', label: 'Best match' },
  { value: 'distance', label: 'Closest to campus' },
  { value: 'rating', label: 'Highest rated' },
];

const distanceOptions = [
  { value: '', label: 'Any distance', description: 'Show every hostel' },
  { value: '1000', label: 'Up to 1 km', description: 'Shortest walks' },
  { value: '2000', label: 'Up to 2 km', description: 'Near campus' },
  { value: '3000', label: 'Up to 3 km', description: 'Wider area' },
];

export function ExplorePage() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get('q') || '');
  const [campus, setCampus] = useState(params.get('campus') || 'uds-tamale-dungu');
  const [budget, setBudget] = useState(() => budgetRangeFromParams(params));
  const [occupancy, setOccupancy] = useState(params.get('occupancy') || '');
  const [distance, setDistance] = useState(params.get('distance') || '');
  const [sort, setSort] = useState('best');
  const [available, setAvailable] = useState(params.get('availability') === 'available');
  const [mobileFilters, setMobileFilters] = useState(false);
  const selectedAmenities = useMemo(() => new Set((params.get('amenity') || '').split(',').filter(Boolean)), [params]);
  const [items, setItems] = useState<HostelSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const selectedCampus = campusOptions.find(option => option.value === campus) ?? campusOptions[0];
  const sortedItems = useMemo(() => {
    if (sort === 'distance') return [...items].sort((a, b) => (a.distanceMeters ?? Number.MAX_SAFE_INTEGER) - (b.distanceMeters ?? Number.MAX_SAFE_INTEGER));
    if (sort === 'rating') return [...items].sort((a, b) => b.averageRating - a.averageRating);
    return items;
  }, [items, sort]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const requestParams = new URLSearchParams(params);
    if (!requestParams.has('campus')) requestParams.set('campus', campus);
    requestParams.set('limit', '50');
    api.get<{ items: HostelSummary[] }>(`/api/public/hostels?${requestParams.toString()}`)
      .then(result => { if (!cancelled) setItems(result.items); })
      .catch(cause => { if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load hostels.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [params]);

  function submit(event: FormEvent) {
    event.preventDefault();
    const next = new URLSearchParams(params);
    query.trim() ? next.set('q', query.trim()) : next.delete('q');
    campus ? next.set('campus', campus) : next.delete('campus');
    applyBudgetRange(next, budget);
    occupancy ? next.set('occupancy', occupancy) : next.delete('occupancy');
    distance ? next.set('distance', distance) : next.delete('distance');
    available ? next.set('availability', 'available') : next.delete('availability');
    setParams(next);
    setMobileFilters(false);
  }

  function toggleAmenity(key: string) {
    const updated = new URLSearchParams(params);
    const next = new Set(selectedAmenities);
    next.has(key) ? next.delete(key) : next.add(key);
    next.size ? updated.set('amenity', [...next].join(',')) : updated.delete('amenity');
    setParams(updated);
  }

  function clearFilters() {
    setQuery('');
    setCampus('uds-tamale-dungu');
    setBudget('');
    setOccupancy('');
    setDistance('');
    setAvailable(false);
    setParams({});
  }

  const hasFilters = Boolean(params.get('q') || params.get('minPrice') || params.get('maxPrice') || params.get('occupancy') || params.get('distance') || params.get('availability') || params.get('amenity'));

  return (
    <div className="explore-page-v2">
      <section className="explore-search-band">
        <div className="market-page">
          <div className="explore-breadcrumb"><Link to="/">Home</Link><span>/</span><strong>Explore hostels</strong></div>
          <h1>Find your place around campus.</h1>
          <form className="explore-search-form" onSubmit={submit}>
            <label className="explore-query-field"><Search size={19}/><input value={query} onChange={event => setQuery(event.target.value)} placeholder="Hostel, area or landmark"/></label>
            <SelectMenu className="explore-search-select" ariaLabel="Choose campus" icon={MapPin} value={campus} onChange={setCampus} options={campusOptions}/>
            <SelectMenu className="explore-search-select" ariaLabel="Choose annual budget" icon={WalletCards} value={budget} onChange={setBudget} options={budgetRanges}/>
            <button className="primary-button large">Search</button>
          </form>
        </div>
      </section>

      <div className="market-page explore-layout-v2">
        <aside className={`filter-panel-v2 ${mobileFilters ? 'open' : ''}`}>
          <div className="filter-panel-head"><div><span>Filters</span><small>Refine your results</small></div><button onClick={() => setMobileFilters(false)} className="mobile-filter-close" aria-label="Close filters"><X size={20}/></button></div>
          <div className="filter-group">
            <div className="filter-group-title"><strong>Availability</strong><ChevronDown size={16}/></div>
            <label className={`check-row ${available ? 'selected' : ''}`}><input type="checkbox" checked={available} onChange={event => setAvailable(event.target.checked)}/><span aria-hidden="true">{available && <Check size={13}/>}</span>Available now</label>
          </div>
          <div className="filter-group">
            <div className="filter-group-title"><strong>Your private annual budget</strong><ChevronDown size={16}/></div>
            <SelectMenu className="filter-select" ariaLabel="Choose annual budget" icon={WalletCards} value={budget} onChange={setBudget} options={budgetRanges}/>
          </div>
          <div className="filter-group">
            <div className="filter-group-title"><strong>Room occupancy</strong><ChevronDown size={16}/></div>
            <SelectMenu className="filter-select" ariaLabel="Choose room occupancy" icon={Users} value={occupancy} onChange={setOccupancy} options={occupancyOptions}/>
          </div>
          <div className="filter-group">
            <div className="filter-group-title"><strong>Facilities</strong><ChevronDown size={16}/></div>
            <div className="check-list">{amenityOptions.map(option => { const selected = selectedAmenities.has(option.key); return <label className={`check-row ${selected ? 'selected' : ''}`} key={option.key}><input type="checkbox" checked={selected} onChange={() => toggleAmenity(option.key)}/><span aria-hidden="true">{selected && <Check size={13}/>}</span>{option.label}</label>; })}</div>
          </div>
          <div className="filter-group">
            <div className="filter-group-title"><strong>Walking distance</strong><Footprints size={16}/></div>
            <p className="distance-reference-note"><Info size={14}/><span>Google walking routes to the verified {selectedCampus.description} campus reference point.</span></p>
            <div className="distance-options">{distanceOptions.map(option => { const selected = distance === option.value; return <button type="button" className={selected ? 'selected' : ''} aria-pressed={selected} onClick={() => setDistance(option.value)} key={option.value || 'any'}><span className="distance-choice-indicator" aria-hidden="true">{selected && <Check size={12}/>}</span><span><strong>{option.label}</strong><small>{option.description}</small></span></button>; })}</div>
          </div>
          <div className="filter-actions"><button className="primary-button full" onClick={() => submit({ preventDefault() {} } as FormEvent)}>Apply filters</button>{hasFilters && <button className="ghost-button full" onClick={clearFilters}>Clear all</button>}</div>
        </aside>

        <main className="explore-results-v2">
          {error && <StatusNotice type="warning">Live data could not load. Showing seeded examples. {error}</StatusNotice>}
          <div className="results-toolbar-v2">
            <div><h2>{loading ? 'Finding hostels…' : `${items.length} hostel${items.length === 1 ? '' : 's'} near ${selectedCampus.description}`}</h2><p>One page per physical hostel, with separate listings from verified agencies.</p></div>
            <div className="results-actions-v2"><button className="mobile-filter-button" onClick={() => setMobileFilters(true)}><SlidersHorizontal size={17}/> Filters</button><div className="sort-control"><span>Sort by</span><SelectMenu className="sort-select" ariaLabel="Sort hostel results" value={sort} onChange={setSort} options={sortOptions}/></div></div>
          </div>
          <div className="active-filters-v2">
            {params.get('availability') === 'available' && <button onClick={() => { setAvailable(false); const next = new URLSearchParams(params); next.delete('availability'); setParams(next); }}>Available now <X size={13}/></button>}
            {(params.get('minPrice') || params.get('maxPrice')) && <button onClick={() => { setBudget(''); const next = new URLSearchParams(params); next.delete('minPrice'); next.delete('maxPrice'); setParams(next); }}>{budgetRangeLabel(budgetRangeFromParams(params))} <X size={13}/></button>}
            {params.get('occupancy') && <button onClick={() => { setOccupancy(''); const next = new URLSearchParams(params); next.delete('occupancy'); setParams(next); }}>{occupancyOptions.find(option => option.value === params.get('occupancy'))?.label || 'Room'} <X size={13}/></button>}
            {params.get('distance') && <button onClick={() => { setDistance(''); const next = new URLSearchParams(params); next.delete('distance'); setParams(next); }}>{distanceOptions.find(option => option.value === params.get('distance'))?.label || 'Distance'} <X size={13}/></button>}
            {[...selectedAmenities].map(key => <button key={key} onClick={() => toggleAmenity(key)}>{amenityOptions.find(item => item.key === key)?.label || key}<X size={13}/></button>)}
            {hasFilters && <button className="clear-filter-chip" onClick={clearFilters}>Clear filters</button>}
          </div>
          {sortedItems.length ? <div className="hostel-grid-v2 results-grid">{sortedItems.map(hostel => <HostelCard key={hostel.id} hostel={hostel}/>)}</div> : <EmptyState title="No matching hostels yet" description="Adjust your filters or submit an open request so matched agencies can send suitable hostel cards."/>}
          <section className="request-inline-cta"><div><Filter size={20}/><span><strong>Can’t find the right match?</strong><small>Tell agencies your budget and priorities once.</small></span></div><Link to="/requests/new" className="secondary-button">Create a request</Link></section>
        </main>
      </div>
    </div>
  );
}
